import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ReferenceLine, ReferenceArea, ReferenceDot, ResponsiveContainer } from 'recharts';

// Generate a centered bell curve on fixed domain [0..100] with mean at 50
const gaussianY = (x, sigma) => Math.exp(-0.5 * Math.pow((x - 50) / sigma, 2));
const genData = (sigma = 18) => {
  const points = [];
  for (let x = 0; x <= 100; x += 100 / 80) {
    points.push({ x: Number(x.toFixed(2)), y: gaussianY(x, sigma) });
  }
  return points;
};

const PulseDot = ({ cx, cy, color = 'var(--succession-primary)' }) => (
  <g>
    <circle cx={cx} cy={cy} r={10} fill={color} opacity="0.3">
      <animate attributeName="r" values="0;14;0" dur="1.6s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.45;0;0.45" dur="1.6s" repeatCount="indefinite" />
    </circle>
    <circle cx={cx} cy={cy} r={3} fill={color} stroke="#ffffff" strokeWidth={1} />
  </g>
);

const mapMarginToX = (m, low, avg, high) => {
  if (!isFinite(m) || !isFinite(low) || !isFinite(avg) || !isFinite(high) || high === low) return 50;
  if (m <= avg) {
    const denom = Math.max(1e-6, (avg - low));
    const t = Math.max(0, Math.min(1, (m - low) / denom));
    return 25 + t * 25; // low -> 25, avg -> 50
  }
  const denom = Math.max(1e-6, (high - avg));
  const t = Math.max(0, Math.min(1, (m - avg) / denom));
  return 50 + t * 25; // avg -> 50, high -> 75
};

const BellCurveChart = ({ low = 0.15, avg = 0.228, high = 0.45, marker = 0.23, className = '' }) => {
  const intl = useIntl();
  const sigma = 18;
  const data = useMemo(() => genData(sigma), [sigma]);
  const pct = (v) => `${Math.round(v * 100)}%`;
  const markerX = mapMarginToX(marker, low, avg, high);
  const markerY = gaussianY(markerX, sigma);

  // Segment datasets so fills only appear under the curve within their ranges
  const segLow = useMemo(() => data.map(p => ({ x: p.x, y: p.x <= 25 ? p.y : 0 })), [data]);
  const segMid = useMemo(() => data.map(p => ({ x: p.x, y: (p.x > 25 && p.x < 75) ? p.y : 0 })), [data]);
  const segHigh = useMemo(() => data.map(p => ({ x: p.x, y: p.x >= 75 ? p.y : 0 })), [data]);
  return (
    <div className={`w-full ${className}`}>
      <div className="text-lg font-semibold text-succession-foreground mb-2">{intl.formatMessage({ id: 'succession.value_acceleration.bell_curve.title' })}</div>
      <div className="w-full h-56 md:h-60 relative">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, left: 0, right: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="bellMid" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--succession-primary)" stopOpacity={0.35} />
                <stop offset="95%" stopColor="var(--succession-primary)" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="bellLow" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--succession-error)" stopOpacity={0.35} />
                <stop offset="95%" stopColor="var(--succession-error)" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="bellHigh" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--succession-success)" stopOpacity={0.35} />
                <stop offset="95%" stopColor="var(--succession-success)" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--succession-border)" />
            <XAxis type="number" dataKey="x" domain={[0, 100]} hide />
            <YAxis hide domain={[0, 1]} />
            {/* Segment fills only under the curve */}
            <Area data={segLow} type="monotone" dataKey="y" stroke="none" fill="url(#bellLow)" />
            <Area data={segMid} type="monotone" dataKey="y" stroke="none" fill="url(#bellMid)" />
            <Area data={segHigh} type="monotone" dataKey="y" stroke="none" fill="url(#bellHigh)" />
            {/* Curve stroke on top */}
            <Area data={data} type="monotone" dataKey="y" fill="transparent" stroke="var(--succession-primary)" strokeWidth={2} isAnimationActive />
            {/* Boundaries at 25% and 75% */}
            <ReferenceLine x={25} stroke="var(--succession-border)" strokeDasharray="3 3" />
            <ReferenceLine x={75} stroke="var(--succession-border)" strokeDasharray="3 3" />
            {/* User marker: dashed line + visible dot */}
            <ReferenceLine x={markerX} stroke="var(--succession-primary)" strokeDasharray="4 2" />
            <ReferenceDot x={markerX} y={markerY} isFront shape={(props) => <PulseDot cx={(props && (props.cx ?? props.x))} cy={(props && (props.cy ?? props.y))} />} label={{ position: 'top', value: intl.formatMessage({ id: 'succession.value_acceleration.bell_curve.you_label' }) }} />
          </AreaChart>
        </ResponsiveContainer>
        {/* Labels underneath for Low / Average / Top */}
        <div className="mt-2 grid grid-cols-3 text-xs">
          <div className="text-left">
            <div className="font-medium text-succession-foreground">{intl.formatMessage({ id: 'succession.value_acceleration.bell_curve.low_performers' })}</div>
            <div className="text-succession-muted">{pct(low)}</div>
          </div>
          <div className="text-center">
            <div className="font-medium text-succession-foreground">{intl.formatMessage({ id: 'succession.value_acceleration.bell_curve.average_performers' })}</div>
            <div className="text-succession-muted">{pct(avg)}</div>
          </div>
          <div className="text-right">
            <div className="font-medium text-succession-foreground">{intl.formatMessage({ id: 'succession.value_acceleration.bell_curve.top_performers' })}</div>
            <div className="text-succession-muted">{pct(high)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BellCurveChart;


